/**
 * TEMP-H-002: Control Plane API Types
 * 
 * TypeScript interfaces for control plane queries and signals.
 * Defines contracts for human-in-the-loop execution control.
 */

import type { StageType } from '@/types/execution';

// Control Queries Response (GET /api/executions/:executionId/queries)
export interface ControlQueries {
  executionId: string;
  currentStage: StageType | null;
  progress: number; // 0-1 range
  attempts: number; // Current iteration count for active stage
  costSoFar: number; // USD spent so far
  qcStatus: 'pending' | 'pass' | 'borderline' | 'fail';
  canControl: boolean; // User permission to control this execution
  lastUpdated: string; // ISO timestamp
}

// Control Signal Payloads
export interface PauseSignal {
  reason?: string;
}

export interface ResumeSignal {
  reason?: string;
}

export interface CancelSignal {
  reason?: string;
}

export interface SkipToStageSignal {
  stage: StageType;
  reason?: string;
}

export interface ApproveSignal {
  stage: StageType;
  notes?: string;
}

export interface RequestRevisionSignal {
  stage: StageType;
  reason: string; // Required for revisions
  specificFeedback?: string;
}

export interface StartMusicMasterSignal {
  reason?: string;
}

export interface StartMusicSidechainSignal {
  reason?: string;
}

// Control Signal Types
export type ControlSignalType = 
  | 'pause'
  | 'resume' 
  | 'cancel'
  | 'skip-to-stage'
  | 'approve'
  | 'request-revision'
  | 'start-music-master'
  | 'start-music-sidechain';

// Control Signal API Response
export interface ControlSignalResponse {
  success: boolean;
  message: string;
  executionId: string;
  signalType: ControlSignalType;
  timestamp: string;
}

// Control Signal Error
export interface ControlSignalError {
  error: string;
  code: string;
  details?: Record<string, any>;
  executionId: string;
  signalType: ControlSignalType;
}

// Permission Checks
export interface ControlPermissions {
  canPause: boolean;
  canResume: boolean;
  canCancel: boolean;
  canSkipStage: boolean;
  canApprove: boolean;
  canRequestRevision: boolean;
  canViewMetrics: boolean;
}

// Query Keys for React Query
export const controlKeys = {
  queries: (executionId: string) => ['control', executionId, 'queries'] as const,
  permissions: (executionId: string) => ['control', executionId, 'permissions'] as const,
} as const;

// WebSocket Control Events
export interface ControlWebSocketEvents {
  'control:signal_sent': {
    executionId: string;
    signalType: ControlSignalType;
    timestamp: string;
    userId: string;
  };
  'control:signal_processed': {
    executionId: string;
    signalType: ControlSignalType;
    success: boolean;
    timestamp: string;
  };
  'control:queries_updated': {
    executionId: string;
    queries: ControlQueries;
    timestamp: string;
  };
}